import { Record } from 'immutable';

const defaultValues: SerializedImmutableStopwatch = {
  id: -1,
  userGroupId: 'nousergroupid',
  startTime: null as number | null,
  measuredTimes: []
};

export class ImmutableStopwatch extends Record(defaultValues) {
  public static create(id: number, userGroupId: string): ImmutableStopwatch {
    return new ImmutableStopwatch({
      id,
      userGroupId,
      startTime: null,
      measuredTimes: []
    });
  }

  public static fromJson(
    data: SerializedImmutableStopwatch
  ): ImmutableStopwatch {
    return new ImmutableStopwatch(data);
  }

  private info: ImmutableStopwatchInfo | null = null;

  private constructor(data: SerializedImmutableStopwatch) {
    super(data);
  }

  public getInfo(): ImmutableStopwatchInfo {
    if (!this.info) {
      this.info = new ImmutableStopwatchInfo(this);
    }

    return this.info;
  }

  public start(): ImmutableStopwatch {
    if (this.startTime) {
      throw new Error('Stopwatch already started');
    }

    return this.set('startTime', Date.now());
  }

  public pause(): ImmutableStopwatch {
    if (this.startTime == null) {
      throw new Error('Stopwatch not started');
    }

    return this.merge({
      startTime: null,
      measuredTimes: [
        ...this.measuredTimes,
        {
          startTime: this.startTime,
          endTime: Date.now()
        }
      ]
    });
  }
}

export class ImmutableStopwatchInfo {
  constructor(private readonly stopwatch: ImmutableStopwatch) {}

  public isStarted(): boolean {
    return this.stopwatch.startTime != null;
  }

  public getId(): number {
    return this.stopwatch.id;
  }

  public getUserGroupId(): string {
    return this.stopwatch.userGroupId;
  }

  public getStartTime(): number | null {
    return this.stopwatch.startTime;
  }

  public getMeasuredTimes(): Array<MeasuredTime> {
    return this.stopwatch.measuredTimes;
  }

  public getDuration(): number {
    let duration = this.stopwatch.measuredTimes.reduce<number>(
      (d, measuredTime) => {
        return d + measuredTime.endTime - measuredTime.startTime;
      },
      0
    );

    if (this.stopwatch.startTime != null) {
      duration += Date.now() - this.stopwatch.startTime;
    }

    return duration;
  }
}

export type SerializedImmutableStopwatch = {
  id: number;
  userGroupId: string;
  startTime: number | null;
  measuredTimes: Array<MeasuredTime>;
};

export type MeasuredTime = {
  startTime: number;
  endTime: number;
};
